const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    display: block;
    font-family: var(--ctl-font);
  }

  .dl{
    display: flex;
    flex-direction: column;
    gap: var(--gap, var(--spacing-md));
  }

  /* layout variants */
  :host([layout="horizontal"]) .dl{
    gap: var(--gap, var(--spacing-sm));
  }

  :host([layout="grid"]) .dl{
    display: grid;
    grid-template-columns: repeat(var(--columns, 2), 1fr);
    gap: var(--gap, var(--spacing-md));
  }

  /* bordered variant */
  :host([bordered]) .dl{
    border: var(--border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg, 14px);
    padding: var(--spacing-md);
    background: var(--ctl-color-surface);
  }

  /* divided variant - adds dividers between items */
  :host([divided]) ::slotted(ctl-description-item:not(:last-child)){
    border-bottom: var(--border-width-thin) solid var(--ctl-color-border);
    padding-bottom: var(--gap, var(--spacing-md));
    margin-bottom: 0 !important;
  }

  /* density */
  :host([density="compact"]) {
    --gap: var(--spacing-sm);
  }

  :host(:not([density]), [density="comfortable"]) {
    --gap: var(--spacing-md);
  }
</style>

<dl class="dl" part="dl">
  <slot></slot>
</dl>
`;

export class CtlDescriptionList extends HTMLElement {
  static get observedAttributes() {
    return ["layout", "density", "columns", "bordered", "divided"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("layout")) this.setAttribute("layout", "vertical");
    if (!this.hasAttribute("density")) this.setAttribute("density", "comfortable");
    this._normalize();
  }

  attributeChangedCallback() {
    this._normalize();
  }

  // attrs
  get layout() { return this.getAttribute("layout") || "vertical"; }
  set layout(v) { this.setAttribute("layout", String(v || "vertical")); }

  get density() { return this.getAttribute("density") || "comfortable"; }
  set density(v) { this.setAttribute("density", String(v || "comfortable")); }

  get columns() { 
    const v = this.getAttribute("columns");
    return v ? parseInt(v, 10) : 2;
  }
  set columns(v) { 
    const n = parseInt(v, 10);
    if (n >= 1 && n <= 6) {
      this.setAttribute("columns", String(n));
      this.style.setProperty("--columns", String(n));
    }
  }

  get bordered() { return this.hasAttribute("bordered"); }
  set bordered(v) { v ? this.setAttribute("bordered", "") : this.removeAttribute("bordered"); }

  get divided() { return this.hasAttribute("divided"); }
  set divided(v) { v ? this.setAttribute("divided", "") : this.removeAttribute("divided"); }

  _normalize() {
    const layout = this.layout;
    if (!["vertical", "horizontal", "grid"].includes(layout)) {
      this.setAttribute("layout", "vertical");
    }

    const density = this.density;
    if (!["comfortable", "compact"].includes(density)) {
      this.setAttribute("density", "comfortable");
    }

    // Update CSS custom property for columns
    if (this.hasAttribute("columns")) {
      this.style.setProperty("--columns", String(this.columns));
    }
  }
}

customElements.define("ctl-description-list", CtlDescriptionList);
